import { convertToArray } from '@dispatch/shared';
import {
  ApiResponseWithPaging,
  Schedule,
  SuperApiResponse,
  TaskListFilterGroup,
} from '@dispatch/shared/types';
import { dispatchClient } from '../clients';
import { mapResponseWithPaging } from '../helpers/AxiosMappers';
import { format } from 'date-fns';
import { isEmpty, isNil, isUndefined, isNull } from 'lodash';
import invariant from 'tiny-invariant';

const APIM_SERVICE_TASKS_URL = 'dispatch/resourceService/resources';
const APIM_SERVICE_SCHEDULE_DATA_URL =
  'dispatch/resourceService/resources/schedules-new';
const APIM_SERVICE_UPSERT_SCHEDULE_URL = 'dispatch/resourceService/schedules';

const getSchedulesApiUrl = function (resourceId: string) {
  return `${APIM_SERVICE_TASKS_URL}/${resourceId}/schedules`;
};

export async function getSchedules(resourceId: string): Promise<Schedule[]> {
  invariant(resourceId, 'Missing ResourceId, cannot fetch schedules');
  const filters: { startDateTime: string } = {
    startDateTime: format(new Date(), 'yyyy-MM-dd'),
  };

  const response = await dispatchClient.get<Schedule[]>(
    getSchedulesApiUrl(resourceId),
    {
      params: {
        ...filters,
      },
    }
  );
  const schedules: Schedule[] = response?.data;

  return schedules;
}

const hasResourceFilters = (filters: { [k: string]: unknown }) => {
  const hasFilters = !isEmpty(
    [
      'resourceStates',
      'resourceCounties',
      'resourceAttributes',
      'employmentType',
      'contingentCompanies',
      'assignedResourceIds',
      'assignedResourceRoleIds',
      'assignedResourceTeamIds',
      'isActive',
      'isContingent',
    ].filter((f) => !isUndefined(filters?.[f]))
  );

  if (!filters.isActive) {
    if (hasFilters) {
      filters.isActive = true;
    } else {
      delete filters.isActive;
    }
  } else {
    delete filters.status;
  }

  return hasFilters ? delete filters.status : '';
};
/**
 * validateFilters searches for filters that were applied by the user to determine if status = assigned needs to be applied
 * @param filters - TaskListFilterGroup
 * @returns - Cleaned filters with undefined and empty array removed.
 */
export const validateFilters = (filters: Record<string, unknown>) => {
  const nonDefaults: [string, unknown][] = [];
  const defaults: [string, unknown][] = [];

  // remove defaults that always exist so we can validate a user filter has been applied
  Object.entries(filters).forEach((filter) => {
    if (
      ![
        'hideCancelledTasks',
        'scheduleEndDate',
        'scheduleStartDate',
        'scheduleTimeZoneId',
        'pageSize',
        'status',
      ].includes(filter[0])
    ) {
      return nonDefaults.push(filter);
    }
    return defaults.push(filter);
  });

  // find any filters that have values, which means a user filter has been applied. Returns
  const foundFilters = nonDefaults.filter(([__, value]) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        // our date pickers will have an array [null, null];
        const checkForNulls = value.flatMap((v) => (!isNull(v) ? v : []));
        return !isEmpty(checkForNulls);
      }

      return !isEmpty(value);
    }

    if (typeof value === 'string') {
      return value !== '';
    }

    return !isNil(value);
  });
  const cleanedFilters = Object.fromEntries([...defaults, ...foundFilters]);

  if (cleanedFilters?.status === 'all') {
    // cannot send in all. It makes it so the api doesnt send the data back correctly
    delete cleanedFilters?.status;
  }

  // if applied filters are found then make status = assigned
  // however if we filter by assigned resource, but the resource has no tasks then nothing will be returned
  if (
    foundFilters?.length > 0 &&
    isEmpty(cleanedFilters.assignedResourceTeamIds) &&
    isEmpty(cleanedFilters.assignedResourceIds)
  ) {
    cleanedFilters.status = 'assigned';
  }

  // resource filters have priority over task, so if they are present we should not filter for status of assigned
  hasResourceFilters(cleanedFilters);

  return cleanedFilters as TaskListFilterGroup;
};

/**
 * Fetches one page of scheduler board data (resources, events, calendars).
 *
 * @param continuationToken - paging token; pass `undefined` for the first page.
 * @param includeClaimOwnerCapacity - asks the API to attach
 *   `resource.claimOwnerCapacity`. REQUIRED, deliberately: there is no default.
 *   The capacity band is gated by a tenant flag AND a per-session toolbar
 *   toggle, and a default here would silently win over both — a caller that
 *   forgot to thread the toggle through would keep paying for the capacity
 *   calculation on every board load. Making it required turns that omission
 *   into a compile error instead of a silent regression. Sending it to an API
 *   build that does not yet honour it is harmless — the property is simply
 *   unrecognised and no `claimOwnerCapacity` comes back.
 */
export async function getScheduleData(
  filters: TaskListFilterGroup,
  continuationToken: string | undefined,
  includeClaimOwnerCapacity: boolean
): Promise<ApiResponseWithPaging<any>> {
  try {
    // Distinguish user filters from default to know whether we should apply status = assigned
    const mappedFilters = mapFilters(filters);
    const validatedFilters = validateFilters(mappedFilters);
    const response = await dispatchClient.post<SuperApiResponse>(
      APIM_SERVICE_SCHEDULE_DATA_URL,
      // includeClaimOwnerCapacity is a response-shaping flag, not a user
      // filter, so it is added AFTER validateFilters. Routing it through the
      // filters object would make validateFilters count it as an applied user
      // filter and force status = 'assigned' on every request.
      { ...validatedFilters, includeClaimOwnerCapacity },
      {
        params: {
          pageSize: filters.pageSize,
        },
        headers: {
          'Continuation-Token': continuationToken || '',
        },
      }
    );

    const mappedResponse = mapResponseWithPaging<SuperApiResponse>(response);
    return {
      ...mappedResponse,
      data: mappedResponse.data || {},
    };
  } catch (e) {
    /* eslint-disable no-console */
    throw new Error('Error fetching schedules');
  }
}

function mapFilters(filters: TaskListFilterGroup) {
  return {
    ...filters,
    assignedResourceIds: getAssignedResourceIds(filters),
    lossCounties: filters?.lossCounties?.map((county) => county.value),
    lossZipCodes: convertToArray(filters?.lossZipCodes),
    resourceCounties: filters?.resourceCounties?.map((county) => county.value),
  };
}

function getAssignedResourceIds(filters: TaskListFilterGroup) {
  const extractIds = (items: any[]): string[] =>
    items.map((x) => (typeof x === 'string' ? x : x.value));

  const resources =
    !!filters.qualifiedResourceIds && filters.qualifiedResourceIds.length > 0
      ? extractIds(filters.qualifiedResourceIds)
      : filters.assignedResourceIds
      ? extractIds(filters.assignedResourceIds)
      : undefined;

  return resources;
}

export async function upsertSchedule(schedule: Schedule) {
  return upsertSchedules([schedule]);
}

export async function upsertSchedules(schedules: Schedule[]) {
  try {
    await dispatchClient.put<Schedule>(
      APIM_SERVICE_UPSERT_SCHEDULE_URL,
      schedules
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function deleteScheduleByExternalId(externalId: string) {
  try {
    await dispatchClient.delete(
      `${APIM_SERVICE_UPSERT_SCHEDULE_URL}/${externalId}`
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}
